import { CommonModule } from "@angular/common"
import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core"
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from "@angular/forms"
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from "@angular/material/autocomplete"
import { MatButtonModule } from "@angular/material/button"
import { MatChipsModule } from "@angular/material/chips"
import { MatFormFieldModule } from "@angular/material/form-field"
import { MatIconModule } from "@angular/material/icon"
import { MatInputModule } from "@angular/material/input"
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner"
import { Subject, Subscription, of } from "rxjs"
import { debounceTime, distinctUntilChanged, switchMap } from "rxjs/operators"
import {
  formatCityLabel,
  formatStateLabel,
  titleCaseCityName,
  UsCity,
  UsState,
} from "../../models/city.model"
import { CityService } from "../../services/city.service"
import { SnackbarService } from "../../services/snackbar.service"
import { StationService } from "../../services/station.service"

/** Minimum characters before master city search API calls and suggestions appear. */
const MIN_SEARCH_LENGTH = 1
const SEARCH_DEBOUNCE_MS = 500
const AUTOCOMPLETE_PANEL_CLASS = "coverage-city-autocomplete-panel"

@Component({
  selector: "app-coverage-city-select",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatIconModule,
    MatChipsModule,
    MatProgressSpinnerModule,
    MatButtonModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CoverageCitySelectComponent),
      multi: true,
    },
  ],
  templateUrl: "./coverage-city-select.component.html",
  styleUrl: "./coverage-city-select.component.css",
})
export class CoverageCitySelectComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() label = "Coverage City"
  @Input() placeholder = "Search & Select City"
  @Input() required = false
  @Input() multiple = false
  @Input() appearance: "outline" | "fill" = "outline"
  @Input() hint = ""
  @Input() disabled = false
  /** When true, limits choices to cities with active station coverage (searchable local catalog). */
  @Input() stationCoverageOnly = false
  /** When true, shows inline form to create a new master city. */
  @Input() allowAddCity = false
  /** When set, locks overflow on this element while the autocomplete panel is open (e.g. '.modal-content'). */
  @Input() scrollLockParentSelector = ""

  @Output() citiesChange = new EventEmitter<UsCity[]>()
  @Output() cityCreated = new EventEmitter<UsCity>()

  @ViewChild("chipSearchInput") chipSearchInput?: ElementRef<HTMLInputElement>
  @ViewChild("singleSearchInput") singleSearchInput?: ElementRef<HTMLInputElement>
  @ViewChild("cityAutocompleteTrigger") cityAutocompleteTrigger?: MatAutocompleteTrigger
  @ViewChild("stateAutocompleteTrigger") stateAutocompleteTrigger?: MatAutocompleteTrigger

  readonly autocompletePanelClass = AUTOCOMPLETE_PANEL_CLASS

  searchControl = new FormControl<string>("", { nonNullable: true })
  newCityNameControl = new FormControl<string>("", { nonNullable: true })
  stateSearchControl = new FormControl<string>("", { nonNullable: true })

  filteredCities: UsCity[] = []
  filteredStates: UsState[] = []
  selectedCities: UsCity[] = []
  selectedCity: UsCity | null = null
  selectedState: UsState | null = null
  loading = false
  statesLoading = false
  creatingCity = false
  loadError = ""
  statesLoadError = ""
  addCityError = ""
  catalogLoaded = false
  showAddCityForm = false

  /** Cities with active station coverage (stationCoverageOnly mode). */
  availableCities: UsCity[] = []
  /** Retained across input resets so option selection still resolves after blur. */
  private lastSearchResults: UsCity[] = []
  private lastStateResults: UsState[] = []

  private search$ = new Subject<string>()
  private stateSearch$ = new Subject<string>()
  private subs = new Subscription()
  private onChange: (value: UsCity | UsCity[] | null) => void = () => {}
  private onTouched: () => void = () => {}
  private scrollLockedElements: HTMLElement[] = []

  constructor(
    private cityService: CityService,
    private stationService: StationService,
    private snackbar: SnackbarService,
    private elementRef: ElementRef<HTMLElement>,
  ) {}

  ngOnInit(): void {
    if (this.stationCoverageOnly) {
      this.loadStationCoverageCatalog()
      this.setupLocalCatalogSearch()
      return
    }

    this.setupApiCitySearch()
  }

  private setupLocalCatalogSearch(): void {
    this.subs.add(
      this.searchControl.valueChanges.subscribe((value) => {
        this.filterLocalCoverageCities((value || "").trim())
      }),
    )
  }

  private filterLocalCoverageCities(query: string): void {
    const pool = this.availableCities.filter(
      (city) => !this.selectedCities.some((selected) => this.compareCities(selected, city)),
    )

    if (!query) {
      this.filteredCities = pool
      return
    }

    const q = query.toLowerCase()
    this.filteredCities = pool.filter((city) => {
      const label = formatCityLabel(city).toLowerCase()
      return label.includes(q) || city.name.toLowerCase().includes(q)
    })
  }

  private setupApiCitySearch(): void {
    this.subs.add(
      this.search$
        .pipe(
          debounceTime(SEARCH_DEBOUNCE_MS),
          distinctUntilChanged(),
          switchMap((term) => {
            const query = term.trim()
            if (query.length < MIN_SEARCH_LENGTH) {
              this.loading = false
              this.loadError = ""
              this.filteredCities = []
              return of([] as UsCity[])
            }
            this.loading = true
            this.loadError = ""
            return this.cityService.searchCities(query, 50)
          }),
        )
        .subscribe({
          next: (cities) => {
            this.filteredCities = cities
            if (cities.length > 0) {
              this.lastSearchResults = cities
            }
            this.loading = false
          },
          error: () => {
            this.loading = false
            this.loadError = "Unable to load cities. Please try again."
            this.filteredCities = []
          },
        }),
    )

    this.subs.add(
      this.stateSearch$
        .pipe(
          debounceTime(SEARCH_DEBOUNCE_MS),
          distinctUntilChanged(),
          switchMap((term) => {
            this.statesLoading = true
            this.statesLoadError = ""
            return this.cityService.searchStates(term.trim(), 50)
          }),
        )
        .subscribe({
          next: (states) => {
            this.filteredStates = states
            if (states.length > 0) {
              this.lastStateResults = states
            }
            this.statesLoading = false
          },
          error: () => {
            this.statesLoading = false
            this.statesLoadError = "Unable to load states. Please try again."
            this.filteredStates = []
          },
        }),
    )

    this.subs.add(
      this.searchControl.valueChanges.subscribe((value) => {
        const query = (value || "").trim()

        if (!this.multiple) {
          if (this.selectedCity && value === formatCityLabel(this.selectedCity)) {
            return
          }
          if (this.selectedCity && value !== formatCityLabel(this.selectedCity)) {
            this.selectedCity = null
            this.emitValue()
          }
        }

        this.search$.next(query)
      }),
    )

    this.subs.add(
      this.stateSearchControl.valueChanges.subscribe((value) => {
        const query = (value || "").trim()
        if (this.selectedState && value === formatStateLabel(this.selectedState)) {
          return
        }
        if (this.selectedState && value !== formatStateLabel(this.selectedState)) {
          this.selectedState = null
        }
        this.stateSearch$.next(query)
      }),
    )
  }

  ngOnDestroy(): void {
    this.closeAllPanels()
    this.unlockScrollContainers()
    this.subs.unsubscribe()
  }

  @HostListener("document:keydown.escape")
  onEscapeKey(): void {
    this.closeAllPanels()
  }

  @HostListener("document:mousedown", ["$event"])
  onDocumentMouseDown(event: MouseEvent): void {
    const target = event.target as Node | null
    if (!target) return

    if (this.elementRef.nativeElement.contains(target)) {
      return
    }

    const panels = document.querySelectorAll(`.${AUTOCOMPLETE_PANEL_CLASS}`)
    for (const panel of Array.from(panels)) {
      if (panel.contains(target)) {
        return
      }
    }

    this.closeAllPanels()
  }

  compareCities = (a: UsCity | null | undefined, b: UsCity | null | undefined): boolean => {
    if (!a || !b) return a === b
    if (a.id != null && b.id != null) return a.id === b.id
    return a.name === b.name && (a.stateCode ?? "") === (b.stateCode ?? "")
  }

  get hasSearchQuery(): boolean {
    return this.searchControl.value.trim().length >= MIN_SEARCH_LENGTH
  }

  get searchPrompt(): string {
    return `Type at least ${MIN_SEARCH_LENGTH} character${MIN_SEARCH_LENGTH === 1 ? "" : "s"} to search cities`
  }

  get emptyOptionsMessage(): string {
    return "No cities found"
  }

  get loadingOptionsMessage(): string {
    return "Searching cities…"
  }

  get selectableCities(): UsCity[] {
    if (this.multiple) {
      return this.filteredCities.filter(
        (city) => !this.selectedCities.some((selected) => this.compareCities(selected, city)),
      )
    }
    return this.filteredCities
  }

  get selectedStateCode(): string {
    return this.selectedState?.stateCode ?? ""
  }

  cityOptionLabel(city: UsCity): string {
    return formatCityLabel(city)
  }

  stateOptionLabel(state: UsState): string {
    return formatStateLabel(state)
  }

  writeValue(value: UsCity | UsCity[] | null): void {
    if (this.multiple) {
      this.selectedCities = Array.isArray(value) ? [...value] : []
      this.resetSearchInput("")
      this.filteredCities = []
      this.emitValue(false)
      return
    }

    this.selectedCity = value && !Array.isArray(value) ? value : null
    this.resetSearchInput(formatCityLabel(this.selectedCity))
    this.filteredCities = []
  }

  registerOnChange(fn: (value: UsCity | UsCity[] | null) => void): void {
    this.onChange = fn
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled
    if (isDisabled) {
      this.searchControl.disable({ emitEvent: false })
      this.newCityNameControl.disable({ emitEvent: false })
      this.stateSearchControl.disable({ emitEvent: false })
    } else {
      this.searchControl.enable({ emitEvent: false })
      this.newCityNameControl.enable({ emitEvent: false })
      this.stateSearchControl.enable({ emitEvent: false })
    }
  }

  markAsTouched(): void {
    this.onTouched()
  }

  onAutocompleteOpened(): void {
    if (this.stationCoverageOnly) {
      this.filterLocalCoverageCities(this.searchControl.value.trim())
    }
    this.lockScrollContainers()
  }

  onAutocompleteClosed(): void {
    this.unlockScrollContainers()
  }

  onStateAutocompleteOpened(): void {
    this.lockScrollContainers()
  }

  onStateAutocompleteClosed(): void {
    this.unlockScrollContainers()
  }

  onCitySelected(event: MatAutocompleteSelectedEvent): void {
    const label = String(event.option.value ?? "").trim()
    const city = this.findCityByLabel(label)
    if (!city) {
      return
    }

    event.option.deselect()

    if (this.multiple) {
      if (!this.selectedCities.some((c) => this.compareCities(c, city))) {
        this.selectedCities = [...this.selectedCities, city].sort((a, b) =>
          a.name.localeCompare(b.name),
        )
      }
      this.getCityAutocompleteTrigger()?.closePanel()
      this.resetSearchInput("")
      this.chipSearchInput?.nativeElement?.blur()
      this.onTouched()
      this.emitValue()
      return
    }

    this.selectedCity = city
    this.resetSearchInput(formatCityLabel(city))
    this.onTouched()
    this.emitValue()
    this.getCityAutocompleteTrigger()?.closePanel()
  }

  onStateSelected(event: MatAutocompleteSelectedEvent): void {
    const label = String(event.option.value ?? "").trim()
    const state = this.findStateByLabel(label)
    if (!state) {
      return
    }
    event.option.deselect()
    this.selectedState = state
    this.stateSearchControl.setValue(formatStateLabel(state), { emitEvent: false })
    this.getStateAutocompleteTrigger()?.closePanel()
  }

  removeCity(city: UsCity, event?: Event): void {
    event?.stopPropagation()
    this.selectedCities = this.selectedCities.filter((c) => !this.compareCities(c, city))
    this.emitValue()
  }

  onBlur(): void {
    this.onTouched()
    if (!this.multiple && this.searchControl.value && !this.selectedCity) {
      this.resetSearchInput("")
    }
  }

  toggleAddCityForm(): void {
    this.showAddCityForm = !this.showAddCityForm
    this.addCityError = ""
    if (this.showAddCityForm) {
      this.newCityNameControl.setValue("")
      this.selectedState = null
      this.stateSearchControl.setValue("", { emitEvent: false })
      this.filteredStates = []
      this.stateSearch$.next("")
    }
  }

  cancelAddCity(): void {
    this.showAddCityForm = false
    this.addCityError = ""
    this.newCityNameControl.setValue("")
    this.selectedState = null
    this.stateSearchControl.setValue("", { emitEvent: false })
  }

  submitNewCity(): void {
    if (this.creatingCity) {
      return
    }

    const name = titleCaseCityName(this.newCityNameControl.value)
    const state = this.selectedState?.state ?? this.selectedState?.name ?? ""

    if (!name) {
      this.addCityError = "City name is required."
      return
    }
    if (!state) {
      this.addCityError = "State is required."
      return
    }

    this.creatingCity = true
    this.addCityError = ""

    this.cityService.createCity(name, state).subscribe({
      next: (city) => {
        this.creatingCity = false
        this.showAddCityForm = false
        this.snackbar.success(`"${formatCityLabel(city)}" was added successfully.`)
        this.cityCreated.emit(city)

        if (this.multiple) {
          if (!this.selectedCities.some((c) => this.compareCities(c, city))) {
            this.selectedCities = [...this.selectedCities, city].sort((a, b) =>
              a.name.localeCompare(b.name),
            )
          }
        } else {
          this.selectedCity = city
          this.resetSearchInput(formatCityLabel(city))
        }

        this.onTouched()
        this.emitValue()
        this.stationService.clearPublicCoverageCitiesCache()
      },
      error: (error) => {
        this.creatingCity = false
        const message =
          error?.error?.error ||
          (typeof error?.error === "string" ? error.error : "") ||
          "Failed to add city. Please try again."
        this.addCityError = message
        if (message.toLowerCase().includes("already exists")) {
          this.snackbar.error("This city already exists.")
        }
      },
    })
  }

  private loadStationCoverageCatalog(): void {
    this.loading = true
    this.loadError = ""
    this.catalogLoaded = false

    this.stationService.getPublicCoverageCities().subscribe({
      next: (cities) => {
        this.availableCities = cities
        this.catalogLoaded = true
        this.loading = false
        this.filterLocalCoverageCities(this.searchControl.value.trim())
      },
      error: () => {
        this.availableCities = []
        this.catalogLoaded = false
        this.loading = false
        this.loadError = "Unable to load coverage cities. Please try again."
      },
    })
  }

  private findCityByLabel(label: string): UsCity | undefined {
    const pools = [this.filteredCities, this.lastSearchResults, this.availableCities]
    for (const pool of pools) {
      const found = pool.find((city) => formatCityLabel(city) === label)
      if (found) {
        return found
      }
    }
    return undefined
  }

  private findStateByLabel(label: string): UsState | undefined {
    const pools = [this.filteredStates, this.lastStateResults]
    for (const pool of pools) {
      const found = pool.find((state) => formatStateLabel(state) === label)
      if (found) {
        return found
      }
    }
    return undefined
  }

  private resetSearchInput(value: string): void {
    this.searchControl.setValue(value, { emitEvent: false })
    this.filteredCities = []
    this.loading = false
    this.loadError = ""

    queueMicrotask(() => {
      this.searchControl.setValue(value, { emitEvent: false })
      const nativeInput = this.multiple
        ? this.chipSearchInput?.nativeElement
        : this.singleSearchInput?.nativeElement
      if (nativeInput) {
        nativeInput.value = value
      }
    })
  }

  private getCityAutocompleteTrigger(): MatAutocompleteTrigger | undefined {
    return this.cityAutocompleteTrigger
  }

  private getStateAutocompleteTrigger(): MatAutocompleteTrigger | undefined {
    return this.stateAutocompleteTrigger
  }

  private closeAllPanels(): void {
    if (this.cityAutocompleteTrigger?.panelOpen) {
      this.cityAutocompleteTrigger.closePanel()
    }
    if (this.stateAutocompleteTrigger?.panelOpen) {
      this.stateAutocompleteTrigger.closePanel()
    }
    this.unlockScrollContainers()
  }

  private lockScrollContainers(): void {
    if (!this.scrollLockParentSelector) {
      return
    }
    this.unlockScrollContainers()
    document.querySelectorAll(this.scrollLockParentSelector).forEach((node) => {
      const element = node as HTMLElement
      element.classList.add("scroll-locked")
      this.scrollLockedElements.push(element)
    })
  }

  private unlockScrollContainers(): void {
    this.scrollLockedElements.forEach((element) => element.classList.remove("scroll-locked"))
    this.scrollLockedElements = []
  }

  private emitValue(notify = true): void {
    if (this.multiple) {
      if (notify) {
        this.onChange([...this.selectedCities])
        this.citiesChange.emit([...this.selectedCities])
      }
      return
    }
    if (notify) {
      this.onChange(this.selectedCity)
    }
  }
}
